import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  SxProps,
  Theme,
} from "@mui/material";
import dayjs from "dayjs";

import { User } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { UserProfile } from "../../components/auth/firestore/getUserDetails";

interface BirthdayFieldProps {
  user: User | null;
  userData: UserProfile | null;
  setUserData: React.Dispatch<React.SetStateAction<UserProfile>>;
  textFieldSx: SxProps<Theme>;
}

const BirthdayField: React.FC<BirthdayFieldProps> = ({
  user,
  userData,
  setUserData,
  textFieldSx,
}) => {
  // ─────────────────────────────────────────────────────────── state
  const [month, setMonth] = useState<number | "">("");
  const [day, setDay] = useState<number | "">("");
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("info");

  // ─────────────────────────────────────────────────────────── helpers
  const functions = getFunctions();
  const updateBirthdayCF = httpsCallable<
    { userID: string; birthday: string },
    { success: boolean; message: string }
  >(functions, "updateBirthdayCF");

  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, i) => dayjs().month(i).format("MMM")), // ["Jan","Feb",...]
    []
  );

  const daysInMonth = useMemo(() => {
    if (month === "") return 31;
    return dayjs(`2024-${String(month + 1).padStart(2, "0")}-01`).daysInMonth(); // 2024 gives leap‑year Feb 29
  }, [month]);

  // preload dropdowns if birthday already saved
  useEffect(() => {
    if (userData?.birthday) {
      const [mm, dd] = userData.birthday.split("-").map(Number);
      setMonth(mm - 1); // month index 0‑based internally
      setDay(dd);
    }
  }, [userData]);

  // ─────────────────────────────────────────────────────────── snackbar
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleCloseSnackbar = (
    _?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // ─────────────────────────────────────────────────────────── clicks
  const handleButtonClick = () => {
    if (!user?.uid) return;

    if (userData?.birthday) {
      showSnackbar("Birthday already set. You cannot change it.", "info");
      return;
    }

    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    if (month === "" || day === "") {
      showSnackbar("Please select month and date before updating.", "warning");
      return;
    }

    setIsConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    setIsConfirmDialogOpen(false);
    if (!user?.uid || month === "" || day === "") return;

    const birthdayString = `${String(month + 1).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`; // MM‑DD

    try {
      const response = await updateBirthdayCF({
        userID: user.uid,
        birthday: birthdayString,
      });

      if (response.data.success) {
        setUserData((prev) => ({ ...prev, birthday: birthdayString }));
        showSnackbar("Birthday updated successfully!", "success");
      } else {
        showSnackbar(
          `Failed to update birthday: ${response.data.message}`,
          "error"
        );
      }
    } catch (err) {
      showSnackbar(
        `Failed to update birthday. ${(err as Error).message}`,
        "error"
      );
    } finally {
      setIsEditing(false);
    }
  };

  // ─────────────────────────────────────────────────────────── render
  if (userData?.birthday) {
    return (
      <Tooltip title="DOB cannot be changed" arrow>
        <Box display="flex" gap={1}>
          <FormControl sx={{ ...textFieldSx, flex: 1 }} disabled>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month">
              {monthNames.map((label, idx) => (
                <MenuItem key={idx} value={idx}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ ...textFieldSx, flex: 1 }} disabled>
            <InputLabel>Day</InputLabel>
            <Select value={day} label="Day">
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="DOB can only be set once." arrow>
        <Box display="flex" gap={1}>
          <FormControl sx={{ ...textFieldSx, flex: 1 }} disabled={!isEditing}>
            <InputLabel>Month</InputLabel>
            <Select
              value={month}
              label="Month"
              onChange={(e) => setMonth(e.target.value as number)}
            >
              {monthNames.map((label, idx) => (
                <MenuItem key={idx} value={idx}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ ...textFieldSx, flex: 1 }} disabled={!isEditing}>
            <InputLabel>Day</InputLabel>
            <Select
              value={day}
              label="Day"
              onChange={(e) => setDay(e.target.value as number)}
            >
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Tooltip>

      <Button
        variant="contained"
        sx={{ mt: 2, fontWeight: "bold", width: "100%" }}
        onClick={handleButtonClick}
      >
        {isEditing ? "Confirm Birthday" : "Set Birthday"}
      </Button>

      {/* confirmation dialog */}
      <Dialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirm Birthday</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Birthday cannot be changed once set. Confirm date?{" "}
            <strong>
              {month !== "" && day !== "" ? `${monthNames[month]} ${day}` : ""}
            </strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BirthdayField;
